/**
 * @typedef {Object} VatTranslations
 * @property {string} vat_valid
 * @property {string} vat_invalid
 * @property {string} vat_checking
 * @property {HTMLElement} errorBanner
 */

/** @type {{ translations: VatTranslations }} */
// @ts-ignore — Theme is a global defined in scripts.liquid
const ThemeRef = window.Theme || Theme;

const VAT_API_PATH = '/apps/dd/vat';
const VAT_STEP = 1;
const COLOR_VALID = '#198754';
const COLOR_INVALID = '#dc3545';

const GLOBO_EVENTS = {
  STEP_MOVE: 'globo.formbuilder.step.move',
  BEFORE_MOVE_STEP: 'globo.formbuilder.form.beforeMoveStep',
  BEFORE_SUBMIT: 'globo.formbuilder.form.beforeSubmit',
  SUBMITTING: 'globo.formbuilder.form.submitting',
  SUBMITTED: 'globo.formbuilder.form.submitted',
};

const SELECTORS = {
  form: '.g-container',
  activePanel: '.panels > .panel.movingIn',
  blockContainer: '.block-container',
  globoError: '.globo-formbuilder-wizard > .wizard__content > .message.error',
  globoErrorContent: '.globo-formbuilder-wizard > .wizard__content > .message.error > .content',
  vatField: '.vat-number-field input',
  vatMessages: '.vat-number-field .messages',
  emailField: 'input[data-type="email"]',
};

/** @type {AbortController | null} */
let vatAbortController = null;

/** @type {boolean | null} */
let lastVatValid = null;

/**
 * Returns the current active step index based on which panel has the `movingIn` class.
 * @returns {number | null}
 */
const getCurrentStep = () => {
  const activePanel = document.querySelector(SELECTORS.activePanel);
  if (!activePanel) return null;

  const step = activePanel.getAttribute('data-step');
  return step !== null ? parseInt(step, 10) : null;
};

/**
 * Grabs the email value from the form.
 * @returns {string}
 */
const getEmail = () => {
  const emailInput = document.querySelector(SELECTORS.emailField);
  return emailInput instanceof HTMLInputElement ? emailInput.value.trim() : '';
};

/**
 * Grabs the VAT number value from the form.
 * @returns {string}
 */
const getVatNumber = () => {
  const vatInput = document.querySelector(SELECTORS.vatField);
  return vatInput instanceof HTMLInputElement ? vatInput.value.trim() : '';
};

/**
 * Shows a validation message below the VAT input field.
 * @param {string} message
 * @param {boolean} isValid
 */
const showVatMessage = (message, isValid) => {
  const messagesEl = document.querySelector(SELECTORS.vatMessages);
  if (!(messagesEl instanceof HTMLElement)) return;

  messagesEl.textContent = message;
  messagesEl.style.color = isValid ? COLOR_VALID : COLOR_INVALID;
};

/**
 * Clears the VAT validation message.
 */
const clearVatMessage = () => {
  const messagesEl = document.querySelector(SELECTORS.vatMessages);
  if (!(messagesEl instanceof HTMLElement)) return;

  messagesEl.textContent = '';
  messagesEl.style.color = '';
};

/**
 * Validates the VAT number against the backend API.
 * @param {string} email
 * @param {string} vatNumber
 * @returns {Promise<boolean>}
 */
const validateVat = async (email, vatNumber) => {
  vatAbortController?.abort();
  vatAbortController = new AbortController();

  const url = new URL(VAT_API_PATH, window.location.origin);
  url.searchParams.set('email', email);
  url.searchParams.set('vat_number', vatNumber);

  try {
    const response = await fetch(url.toString(), {
      signal: vatAbortController.signal,
    });

    if (!response.ok) return false;

    const data = await response.json();
    return data.valid === true;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return false;
    }
    return false;
  }
};

/**
 * Handles VAT input change: calls the API and displays the result.
 */
const handleVatChange = async () => {
  const email = getEmail();
  const vatNumber = getVatNumber();

  if (!vatNumber) {
    clearVatMessage();
    lastVatValid = null;
    return;
  }

  lastVatValid = null;
  showVatMessage(ThemeRef.translations.vat_checking, true);

  const isValid = await validateVat(email, vatNumber);
  lastVatValid = isValid;

  const message = isValid ? ThemeRef.translations.vat_valid : ThemeRef.translations.vat_invalid;
  showVatMessage(message, isValid);
};

/**
 * Attaches a change listener to the VAT input when it becomes available.
 */
const observeVatInput = () => {
  const vatInput = document.querySelector(SELECTORS.vatField);
  if (!(vatInput instanceof HTMLInputElement)) return;

  vatInput.addEventListener('change', handleVatChange);
};

document.addEventListener(GLOBO_EVENTS.STEP_MOVE, () => {
  observeVatInput();
});

document.addEventListener(GLOBO_EVENTS.BEFORE_MOVE_STEP, (event) => {
  if (!document.querySelector(SELECTORS.vatField)) return;

  const currentStep = getCurrentStep();

  if (currentStep !== VAT_STEP) return;

  if (lastVatValid === false) {
    showVatMessage(ThemeRef.translations.vat_invalid, false);
    event.preventDefault();
    event.stopPropagation();
    return;
  }

  if (lastVatValid === null && getVatNumber()) {
    showVatMessage(ThemeRef.translations.vat_invalid, false);
    event.preventDefault();
    event.stopPropagation();
  }
});

/**
 * Shows our custom error banner.
 * @param {string} message
 */
const showCustomError = (message) => {
  let errorBanner = document.querySelector('#custom-vat-error');
  if (!errorBanner) {
    errorBanner = document.createElement('div');
    errorBanner.id = 'custom-vat-error';
    errorBanner.classList.add('custom-vat-error');
    
    const form = document.querySelector(SELECTORS.form);
    if (form) {
      form.prepend(errorBanner);
    }
  }
  
  errorBanner.textContent = message;
  errorBanner.style.display = 'block';
};

/**
 * Hides our custom error banner.
 */
const hideCustomError = () => {
  const errorBanner = document.querySelector('#custom-vat-error');
  if (errorBanner) {
    errorBanner.style.display = 'none';
  }
};

/**
 * Blocks form submission when VAT is invalid or not yet validated.
 * @param {Event} event
 */
const blockSubmitIfVatInvalid = (event) => {
  if (!document.querySelector(SELECTORS.vatField)) return;

  if (lastVatValid === true) {
    hideCustomError();
    return true;
  }

  showCustomError(ThemeRef.translations.vat_invalid);
  
  // Try everything to stop the submission
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
  
  return false;
};

document.addEventListener(GLOBO_EVENTS.BEFORE_SUBMIT, blockSubmitIfVatInvalid);
document.addEventListener(GLOBO_EVENTS.SUBMITTING, blockSubmitIfVatInvalid);

// Add a capturing submit listener to forcefully stop it
document.addEventListener('submit', (e) => {
  const form = document.querySelector(SELECTORS.form);
  if (form && form.contains(e.target)) {
    if (document.querySelector(SELECTORS.vatField) && lastVatValid !== true) {
      showCustomError(ThemeRef.translations.vat_invalid);
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }
  }
}, true);

// Add a capturing click listener on submit buttons
document.addEventListener('click', (e) => {
  const target = e.target;
  const isSubmitBtn = target instanceof Element && (target.matches('button[type="submit"], input[type="submit"]') || target.closest('button[type="submit"], input[type="submit"]'));
  const form = document.querySelector(SELECTORS.form);
  
  if (isSubmitBtn && form && form.contains(target)) {
    if (document.querySelector(SELECTORS.vatField) && lastVatValid !== true) {
      showCustomError(ThemeRef.translations.vat_invalid);
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }
  }
}, true);

observeVatInput();
